import { useEffect, useState, type ReactElement } from 'react';
import { api, type CurrentUser } from './api';
import { Shell } from './components/Shell';
import { t } from './i18n';
import { Agents } from './pages/Agents';
import { Approvals } from './pages/Approvals';
import { Finance } from './pages/Finance';
import { Inbox } from './pages/Inbox';
import { Knowledge } from './pages/Knowledge';
import { Audit } from './pages/Audit';
import { Connect } from './pages/Connect';
import { Dialogs } from './pages/Dialogs';
import { Invite } from './pages/Invite';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Processes } from './pages/Processes';
import { Profile } from './pages/Profile';
import { RunPage } from './pages/RunPage';
import { Runs } from './pages/Runs';
import { TaskPage } from './pages/TaskPage';
import { Tasks } from './pages/Tasks';
import { Team } from './pages/Team';
import { matchPath, navigate, usePath } from './router';

type AuthState = { kind: 'loading' } | { kind: 'anonymous' } | { kind: 'authed'; user: CurrentUser };

function can(user: CurrentUser, action: string): boolean {
  return user.permissions.some((p) => p === '*' || p === action || (p.endsWith('.*') && action.startsWith(p.slice(0, -1))));
}

function Placeholder({ title, phase }: { title: string; phase: number }) {
  return (
    <>
      <h1>{title}</h1>
      <div className="card muted">{t('common.phase', { n: phase })}</div>
    </>
  );
}

export function App() {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });
  const path = usePath();

  useEffect(() => {
    api.me().then((user) => setState({ kind: 'authed', user })).catch(() => setState({ kind: 'anonymous' }));
  }, []);

  const invite = matchPath('/invite/:token', path);
  if (invite) return <Invite token={invite.token!} />;

  if (state.kind === 'loading') return <div className="content muted">{t('common.loading')}</div>;
  if (state.kind === 'anonymous') {
    return (
      <Login
        onLogin={(user) => {
          setState({ kind: 'authed', user });
          if (path !== '/') navigate('/');
        }}
      />
    );
  }

  const user = state.user;
  const setUser = (u: CurrentUser) => setState({ kind: 'authed', user: u });
  const logout = () => void api.logout().finally(() => setState({ kind: 'anonymous' }));

  // The consent page has no shell: an assistant sent the browser here and takes it back afterwards.
  if (path === '/connect') return <Connect user={user} />;

  let page: ReactElement;
  switch (path) {
    case '/':
      page = <Overview canManage={can(user, 'business.manage')} canWriteTasks={can(user, 'task.write')} canConnect={can(user, 'external.manage')} />;
      break;
    case '/team':
      page = <Team canInvite={can(user, 'team.invite')} canManageKeys={can(user, 'credential.manage')} />;
      break;
    case '/profile':
      page = <Profile user={user} onUserChange={setUser} />;
      break;
    case '/agents':
      page = <Agents canRead={can(user, 'agent.read')} canManage={can(user, 'agent.manage')} canManageTools={can(user, 'tool.manage')} canManageModels={can(user, 'model.manage')} canManageLibrary={can(user, 'library.manage')} />;
      break;
    case '/tasks':
      page = <Tasks canWrite={can(user, 'task.write')} />;
      break;
    case '/inbox':
      page = can(user, 'task.read') ? <Inbox canWrite={can(user, 'task.write')} /> : <Placeholder title={t('nav.inbox')} phase={3} />;
      break;
    case '/dialogs':
      page = can(user, 'task.read') ? <Dialogs /> : <Placeholder title={t('nav.dialogs')} phase={3} />;
      break;
    case '/processes':
      page = <Processes canManage={can(user, 'process.manage')} />;
      break;
    case '/finance':
      page = <Finance canRead={can(user, 'finance.read')} canReadSources={can(user, 'external.read')} canManage={can(user, 'external.manage')} />;
      break;
    case '/plans':
      page = <Placeholder title={t('nav.plans')} phase={5} />;
      break;
    case '/approvals':
      page = <Approvals canDecide={can(user, 'approval.decide')} onPendingCount={(n) => setUser({ ...user, pendingApprovals: n })} />;
      break;
    case '/runs':
      page = can(user, 'run.read') ? <Runs /> : <Placeholder title={t('nav.runs')} phase={2} />;
      break;
    case '/knowledge':
      page = can(user, 'knowledge.read') ? <Knowledge canWrite={can(user, 'knowledge.write')} /> : <Placeholder title={t('nav.knowledge')} phase={4} />;
      break;
    case '/audit':
      page = <Audit />;
      break;
    default: {
      const task = matchPath('/tasks/:id', path);
      const run = matchPath('/runs/:id', path);
      page = task ? (
        <TaskPage id={task.id!} canWrite={can(user, 'task.write')} canStartRun={can(user, 'run.start')} currentActorId={user.actorId} />
      ) : run && can(user, 'run.read') ? (
        <RunPage id={run.id!} canManage={can(user, 'run.manage')} canStart={can(user, 'run.start')} />
      ) : (
        <Placeholder title={t('home.title')} phase={1} />
      );
    }
  }

  return (
    <Shell user={user} onLogout={logout}>
      {page}
    </Shell>
  );
}
